/**
 * Keycloak configuration (BFF pattern).
 *
 * The frontend does NOT exchange the authorization code for tokens.
 * Instead it extracts the code from the Keycloak redirect and sends it
 * to the backend, which performs the token exchange server-side.
 *
 * For health check, redirect and URL helpers use @/api/keycloak_api.
 *
 * We use sessionStorage for OAuth state, nonce, and session_state between
 * navigations. The Pinia store holds only session_state (mirrored from sessionStorage).
 * Keys: oauthStateStorageKey, oauthNonceStorageKey, oauthSessionStateStorageKey.
 */

import router from '@/router'
import type { RouteRecordRaw } from 'vue-router'
import runtimeConfig from '@/config/runtime_config'

const keycloakUrl = runtimeConfig.KEYCLOAK_URL ?? 'http://localhost:8080'
const keycloakRealm = runtimeConfig.KEYCLOAK_REALM ?? 'plyas'

const keycloakConfig = {
  url: keycloakUrl,
  healthCheckUrl: runtimeConfig.KEYCLOAK_HEALTH_CHECK_URL ?? 'http://localhost:9000',
  healthCheckIntervalSeconds: Number(runtimeConfig.KEYCLOAK_HEALTH_CHECK_INTERVAL_SECONDS ?? 10),
  realm: keycloakRealm,
  clientId: runtimeConfig.KEYCLOAK_CLIENT_ID ?? 'plyas',
  /** Path for OAuth redirect (Welcome route). */
  redirectUri: `${window.location.origin}${router.getRoutes().find((route: RouteRecordRaw) => route.name === 'Welcome')?.path ?? ''}`,
  issuer: `${keycloakUrl}/realms/${keycloakRealm}`,
  /** sessionStorage key for OAuth state (persisted between reloads for callback validation). */
  oauthStateStorageKey: 'keycloak_oauth_state',
  /** sessionStorage key for OAuth nonce (persisted between reloads). */
  oauthNonceStorageKey: 'keycloak_oauth_nonce',
  /** sessionStorage key for Keycloak session_state (for logout / session checks). */
  oauthSessionStateStorageKey: 'keycloak_oauth_session_state',
}

/** Default export for code that may import default keycloak. */
export default {
  ...keycloakConfig,
}
